import React, { useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { createPortal } from 'react-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import SubMatFolderIcon from '../G04docuSubMatFolder/SubMatFolderIcon';
import { buildChildrenMap, headingHasDocuments, isHeadingItem, shouldShowTestComparisonCheckbox, isTestComparisonMasterRoot, collectDescendantComparisonIds, itemHasTestComparison } from './subMatTemplateTocOrder';

const HeadingDraft = ({ initial = '', allowEmpty = false, onCommit, onCancel }) => {
  const [value, setValue] = useState(initial);
  const valueRef = useRef(initial);
  const inputRef = useRef(null);
  const done = useRef(false);
  const finish = (text) => {
    if (done.current) return;
    done.current = true;
    const name = (text || '').trim();
    if (name || allowEmpty) onCommit(name);
    else onCancel();
  };
  useEffect(() => {
    const focus = () => inputRef.current?.focus();
    focus();
    const timer = setTimeout(focus, 0);
    return () => clearTimeout(timer);
  }, []);
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, minHeight: 32, px: '8px', py: 0.25 }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <TextField
        inputRef={inputRef}
        autoFocus
        fullWidth
        size="small"
        value={value}
        onChange={(e) => {
          valueRef.current = e.target.value;
          setValue(e.target.value);
        }}
        onBlur={() => finish(valueRef.current)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            finish(valueRef.current);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            done.current = true;
            onCancel();
          }
        }}
        inputProps={{ 'aria-label': '제목' }}
        sx={{
          '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0.5 },
        }}
      />
    </Box>
  );
};

const collectDescendantIds = (itemId, childrenMap) => {
  const ids = [itemId];
  (childrenMap[itemId] || []).forEach((k) => {
    ids.push(...collectDescendantIds(k.id, childrenMap));
  });
  return ids;
};

const displayTitle = (item) => {
  if (item.titleOverride?.trim()) return item.titleOverride.trim();
  return item.folderName || item.folderId;
};

/** dropPosition: before | after | inside */
const applyDrop = (items, dragId, targetId, dropPosition) => {
  if (!dragId || !targetId || dragId === targetId) return items;
  const childrenMap = buildChildrenMap(items);
  const descendants = new Set(collectDescendantIds(dragId, childrenMap));
  if (descendants.has(targetId)) return items;

  const target = items.find((it) => it.id === targetId);
  const drag = items.find((it) => it.id === dragId);
  if (!target || !drag) return items;

  const newParentId = dropPosition === 'inside'
    ? target.id
    : (target.parentItemId || null);

  const parentKey = newParentId || '';
  const siblings = (childrenMap[parentKey] || [])
    .filter((s) => s.id !== dragId)
    .map((s) => s.id);

  let insertAt;
  if (dropPosition === 'inside') {
    insertAt = siblings.length;
  } else {
    const targetIdx = siblings.indexOf(targetId);
    insertAt = dropPosition === 'before' ? targetIdx : targetIdx + 1;
    if (insertAt < 0) insertAt = siblings.length;
  }
  siblings.splice(insertAt, 0, dragId);

  const orderById = {};
  siblings.forEach((id, i) => { orderById[id] = i + 1; });

  return items.map((it) => {
    if (it.id === dragId) {
      return {
        ...it,
        parentItemId: newParentId,
        sortOrder: orderById[it.id],
      };
    }
    if (orderById[it.id] != null) {
      return { ...it, sortOrder: orderById[it.id] };
    }
    return it;
  });
};

const resolveDropPosition = (e, el) => {
  const rect = el.getBoundingClientRect();
  const ratio = (e.clientY - rect.top) / Math.max(rect.height, 1);
  if (ratio < 0.22) return 'before';
  if (ratio > 0.78) return 'after';
  return 'inside';
};

const TocRow = ({
  item,
  hasChildren,
  expanded,
  onToggleExpand,
  onUpdate,
  onToggleIncluded,
  onToggleComparison,
  childrenMap,
  items,
  onContextMenu,
  dragIdRef,
  onDragStart,
  onDragEnd,
  onDragOverRow,
  onDropOnRow,
  selectedId = null,
  inlineRenameId = null,
  onSelect,
  onBeginRename,
  onCommitRename,
  onCancelRename,
  onPreview,
  previewLoadingId,
  compact = false,
  onMoveSibling,
  moveMode = false,
  moveSourceId = null,
  onPickRow,
}) => {
  const pressTimer = useRef(null);
  const pressStart = useRef(null);
  const clearPress = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };
  useEffect(() => clearPress, []);

  const folderStub = { currentFile: item.hasCurrentFile ? {} : null };
  const selected = selectedId === item.id;
  const renaming = inlineRenameId === item.id;
  const subtreeIds = collectDescendantIds(item.id, childrenMap);
  const includedCount = subtreeIds.filter((id) => (
    items.find((it) => it.id === id)?.included !== false
  )).length;
  const includedChecked = includedCount === subtreeIds.length;
  const includedIndeterminate = includedCount > 0 && includedCount < subtreeIds.length;
  const showComparisonCheckbox = shouldShowTestComparisonCheckbox(item, childrenMap);
  const descendantCmpIds = isTestComparisonMasterRoot(item)
    ? collectDescendantComparisonIds(item.id, childrenMap)
    : [];
  const comparisonTargets = itemHasTestComparison(item)
    ? [item.id, ...descendantCmpIds.filter((id) => id !== item.id)]
    : descendantCmpIds;
  const checkedCount = comparisonTargets.filter((id) => (
    items.find((it) => it.id === id)?.includeTestComparison === true
  )).length;
  const comparisonChecked = comparisonTargets.length > 0
    ? checkedCount === comparisonTargets.length
    : item.includeTestComparison === true;
  const comparisonIndeterminate = comparisonTargets.length > 0
    && checkedCount > 0
    && checkedCount < comparisonTargets.length;
  const heading = isHeadingItem(item);
  const headingEmpty = heading && !headingHasDocuments(item, childrenMap);
  const siblings = childrenMap[item.parentItemId || ''] || [];
  const siblingIndex = siblings.findIndex((s) => s.id === item.id);
  const canMoveUp = siblingIndex > 0;
  const canMoveDown = siblingIndex >= 0 && siblingIndex < siblings.length - 1;

  const comparisonControl = showComparisonCheckbox ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: compact ? 'flex-start' : 'center',
        flexShrink: 0,
        pl: compact ? 3.5 : 0,
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <Checkbox
        size="small"
        checked={comparisonChecked}
        indeterminate={comparisonIndeterminate}
        onChange={(e) => onToggleComparison(item.id, e.target.checked)}
        sx={{ p: 0.25 }}
      />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={compact ? { whiteSpace: 'normal', lineHeight: 1.3, pt: 0.5 } : { whiteSpace: 'nowrap' }}
      >
        시험성과대비표 합본 포함
      </Typography>
    </Box>
  ) : null;

  return (
    <Box
      draggable={!compact && !renaming}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', item.id);
        dragIdRef.current = item.id;
        e.currentTarget.style.opacity = '0.35';
        onDragStart();
      }}
      onDragEnd={(e) => {
        e.currentTarget.style.opacity = '';
        onDragEnd();
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        onDragOverRow(e.currentTarget, resolveDropPosition(e, e.currentTarget));
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const id = e.dataTransfer.getData('text/plain') || dragIdRef.current;
        onDropOnRow(id, item.id, resolveDropPosition(e, e.currentTarget));
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        onContextMenu(e, item);
      }}
      onTouchStart={(e) => {
        if (!compact || moveMode) return;
        if (e.target.closest('button, input, a, [role="button"]')) return;
        const t = e.touches[0];
        pressStart.current = { x: t.clientX, y: t.clientY };
        clearPress();
        pressTimer.current = setTimeout(() => {
          const start = pressStart.current;
          if (!start) return;
          onContextMenu({
            preventDefault() {},
            clientX: start.x,
            clientY: start.y,
          }, item, true);
        }, 500);
      }}
      onTouchMove={(e) => {
        const start = pressStart.current;
        if (!start) return;
        const t = e.touches[0];
        if (Math.abs(t.clientX - start.x) > 8 || Math.abs(t.clientY - start.y) > 8) {
          clearPress();
        }
      }}
      onClick={(e) => {
        if (e.target.closest('button, input, a, [role="button"]')) return;
        if (compact && moveMode && onPickRow) {
          onPickRow(item, e.currentTarget);
          return;
        }
        if (!compact) onSelect?.(item.id);
      }}
      onDoubleClick={(e) => {
        if (compact || !onBeginRename) return;
        if (e.target.closest('button, input, a, [role="button"]')) return;
        e.preventDefault();
        onBeginRename(item);
      }}
      onTouchEnd={clearPress}
      onTouchCancel={clearPress}
      sx={{
        display: 'flex',
        alignItems: compact ? 'flex-start' : 'center',
        px: '8px',
        py: 0.25,
        minHeight: 32,
        opacity: (item.included === false || headingEmpty) ? 0.45 : 1,
        cursor: moveMode ? 'pointer' : (compact ? 'default' : 'grab'),
        userSelect: 'none',
        bgcolor: moveSourceId === item.id ? 'action.selected' : 'transparent',
        boxShadow: 'none',
        outline: selected ? '1px solid' : 'none',
        outlineOffset: '-1px',
        outlineColor: 'primary.main',
        '&:hover': { bgcolor: 'action.hover' },
        '&[data-drop="inside"], &[data-drop="inside"]:hover': { bgcolor: 'action.selected' },
        '&[data-drop="before"], &[data-drop="before"]:hover': {
          bgcolor: 'transparent',
          boxShadow: (theme) => `inset 0 2px 0 ${theme.palette.primary.main}`,
        },
        '&[data-drop="after"], &[data-drop="after"]:hover': {
          bgcolor: 'transparent',
          boxShadow: (theme) => `inset 0 -2px 0 ${theme.palette.primary.main}`,
        },
        '&:active': { cursor: 'grabbing' },
      }}
    >
      {/* MUI TreeItem iconContainer: width 15, marginRight 4 */}
      <Box
        sx={{
          width: 15,
          mr: '4px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& svg': { fontSize: 18 },
        }}
      >
        {hasChildren ? (
          <Box
            component="span"
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(item.id);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onToggleExpand(item.id);
              }
            }}
            sx={{
              display: 'inline-flex',
              cursor: 'pointer',
              lineHeight: 0,
              color: 'inherit',
            }}
          >
            {expanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
          </Box>
        ) : null}
      </Box>
      {/* MUI TreeItem label: paddingLeft 4 */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minWidth: 0,
          pl: '4px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            minWidth: 0,
          }}
        >
        <Checkbox
          size="small"
          checked={includedChecked}
          indeterminate={includedIndeterminate}
          onChange={(e) => onToggleIncluded(item.id, e.target.checked)}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{ p: 0.25 }}
        />
        {!heading && <SubMatFolderIcon folder={folderStub} />}
        {renaming ? (
          <HeadingDraft
            initial={item.titleOverride || item.folderName || ''}
            allowEmpty={!heading}
            onCommit={onCommitRename}
            onCancel={onCancelRename}
          />
        ) : (
          <Typography
            variant="body2"
            component="span"
            noWrap
            sx={{ flex: 1, minWidth: 0 }}
            color={item.folderMissing ? 'error.main' : (headingEmpty ? 'text.disabled' : 'text.primary')}
          >
            {displayTitle(item)}
            {item.folderMissing && ' (폴더 없음)'}
          </Typography>
        )}
        {compact && onMoveSibling && !moveMode ? (
          <Box sx={{ display: 'inline-flex', flexShrink: 0 }} onTouchStart={(e) => e.stopPropagation()}>
            <IconButton
              size="small"
              aria-label="위로"
              disabled={!canMoveUp}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onMoveSibling(item.id, 'up');
              }}
              sx={{ p: 0.25 }}
            >
              <KeyboardArrowUpIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              aria-label="아래로"
              disabled={!canMoveDown}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onMoveSibling(item.id, 'down');
              }}
              sx={{ p: 0.25 }}
            >
              <KeyboardArrowDownIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : null}
        {onPreview && item.hasCurrentFile === true && item.folderId && !item.folderMissing && (
          <Tooltip
            title="문서 보기"
            placement="top"
            enterDelay={400}
            disableInteractive
            PopperProps={{ sx: { pointerEvents: 'none' } }}
          >
            <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }} onTouchStart={(e) => e.stopPropagation()}>
              <IconButton
                size="small"
                aria-label="문서 보기"
                disabled={previewLoadingId === item.id}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onPreview?.(item);
                }}
                sx={{ p: 0.25 }}
              >
                {previewLoadingId === item.id
                  ? <CircularProgress size={16} />
                  : <VisibilityIcon fontSize="small" />}
              </IconButton>
            </Box>
          </Tooltip>
        )}
        {!compact && comparisonControl}
        </Box>
        {compact && comparisonControl}
      </Box>
    </Box>
  );
};

/** MUI TreeItem-group: marginLeft 17 only */
const TocBranch = ({
  nodes,
  childrenMap,
  expandedSet,
  onToggleExpand,
  draft,
  onCommitDraft,
  onCancelDraft,
  dragIdRef,
  onDragOverRow,
  onDropOnRow,
  ...rowProps
}) => (
  <>
    {nodes.map((item) => {
      const kids = childrenMap[item.id] || [];
      const hasChildren = kids.length > 0;
      const open = expandedSet.has(item.id);
      const parentDraft = draft?.anchorId === item.id && draft.placement === 'parent';
      const afterDraft = draft?.anchorId === item.id && draft.placement === 'after';
      const childDraft = draft?.anchorId === item.id && draft.placement === 'child';
      const branch = (
        <>
          <TocRow
            item={item}
            hasChildren={hasChildren}
            expanded={open}
            onToggleExpand={onToggleExpand}
            childrenMap={childrenMap}
            dragIdRef={dragIdRef}
            onDragOverRow={onDragOverRow}
            onDropOnRow={onDropOnRow}
            {...rowProps}
          />
          {((hasChildren && open) || childDraft) && (
            <Box sx={{ ml: '17px' }}>
              {hasChildren && open && (
                <TocBranch
                  nodes={kids}
                  childrenMap={childrenMap}
                  expandedSet={expandedSet}
                  onToggleExpand={onToggleExpand}
                  draft={draft}
                  onCommitDraft={onCommitDraft}
                  onCancelDraft={onCancelDraft}
                  dragIdRef={dragIdRef}
                  onDragOverRow={onDragOverRow}
                  onDropOnRow={onDropOnRow}
                  {...rowProps}
                />
              )}
              {childDraft && (
                <HeadingDraft onCommit={onCommitDraft} onCancel={onCancelDraft} />
              )}
            </Box>
          )}
        </>
      );
      return (
        <Box key={item.id}>
          {parentDraft && (
            <HeadingDraft onCommit={onCommitDraft} onCancel={onCancelDraft} />
          )}
          {parentDraft ? <Box sx={{ ml: '17px' }}>{branch}</Box> : branch}
          {afterDraft && (
            <HeadingDraft onCommit={onCommitDraft} onCancel={onCancelDraft} />
          )}
        </Box>
      );
    })}
  </>
);

/** sendMode: 발송 직전 — 제외·순서 + 표시명 변경(팝업). 제거는 숨김
 *  bare: 바깥 테두리 없음 (SendSection 안에 넣을 때)
 */
const SubMatTemplateTocTree = ({
  items,
  onChange,
  sendMode = false,
  bare = false,
  onPreview,
  previewLoadingId = '',
  /** 있으면 모바일 목차 이동 버튼을 이 엘리먼트(섹션 헤더)에 붙인다 */
  moveSlotEl = null,
  /** 헤더 슬롯을 쓸 때는, 슬롯이 붙기 전에 목록 위에 버튼이 잠깐 보이지 않게 한다 */
  preferHeaderMove = false,
}) => {
  const [expanded, setExpanded] = useState(() => items.map((it) => it.id));
  const [menu, setMenu] = useState(null);
  const [headingDraft, setHeadingDraft] = useState(null);
  const [inlineRenameId, setInlineRenameId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [moveMode, setMoveMode] = useState(false);
  const [moveSourceId, setMoveSourceId] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null);
  const dragIdRef = useRef(null);
  const dropMarkRef = useRef(null);
  const listRef = useRef(null);
  const clearDropMark = () => {
    const prev = dropMarkRef.current;
    if (prev?.el) delete prev.el.dataset.drop;
    dropMarkRef.current = null;
  };
  const markDrop = (el, position) => {
    const prev = dropMarkRef.current;
    if (prev?.el === el && prev.position === position) return;
    if (prev?.el) delete prev.el.dataset.drop;
    el.dataset.drop = position;
    dropMarkRef.current = { el, position };
  };
  const endDragVisual = () => {
    clearDropMark();
    dragIdRef.current = null;
    if (listRef.current) listRef.current.style.paddingBottom = '';
  };
  const ignoreMenuClose = useRef(false);
  const pendingDraftRef = useRef(null);
  const pendingRenameRef = useRef(null);

  const childrenMap = useMemo(() => buildChildrenMap(items), [items]);
  const roots = childrenMap[''] || [];
  const expandedSet = useMemo(() => new Set(expanded), [expanded]);
  const seenIdsRef = useRef(new Set(items.map((it) => it.id)));

  useEffect(() => {
    const currentIds = items.map((it) => it.id);
    const currentSet = new Set(currentIds);
    const added = currentIds.filter((id) => !seenIdsRef.current.has(id));
    seenIdsRef.current.forEach((id) => {
      if (!currentSet.has(id)) seenIdsRef.current.delete(id);
    });
    added.forEach((id) => seenIdsRef.current.add(id));
    setExpanded((prev) => {
      const kept = prev.filter((id) => currentSet.has(id));
      if (!added.length && kept.length === prev.length) return prev;
      return [...kept, ...added];
    });
  }, [items]);

  const onUpdate = (id, patch) => {
    onChange(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  const onToggleIncluded = (id, checked) => {
    const ids = new Set(collectDescendantIds(id, childrenMap));
    onChange(items.map((it) => (
      ids.has(it.id) ? { ...it, included: checked } : it
    )));
  };

  const onToggleComparison = (id, checked) => {
    const item = items.find((it) => it.id === id);
    if (!item) return;
    const ids = new Set();
    if (isTestComparisonMasterRoot(item)) {
      collectDescendantComparisonIds(id, childrenMap).forEach((cid) => ids.add(cid));
    }
    if (itemHasTestComparison(item) || isTestComparisonMasterRoot(item)) {
      ids.add(id);
    }
    if (!ids.size) return;
    onChange(items.map((it) => (
      ids.has(it.id) ? { ...it, includeTestComparison: checked } : it
    )));
  };

  const onRemove = (id) => {
    const item = items.find((it) => it.id === id);
    if (item && isHeadingItem(item)) {
      const kids = childrenMap[id] || [];
      if (!kids.length) {
        onChange(items.filter((it) => it.id !== id));
        return;
      }
      const parentKey = item.parentItemId || '';
      const siblings = (childrenMap[parentKey] || []).filter((s) => s.id !== id);
      const idx = (childrenMap[parentKey] || []).findIndex((s) => s.id === id);
      const insertAt = idx < 0 ? siblings.length : idx;
      const nextSiblings = [
        ...siblings.slice(0, insertAt),
        ...kids,
        ...siblings.slice(insertAt),
      ];
      const orderById = {};
      nextSiblings.forEach((s, i) => { orderById[s.id] = i + 1; });
      const kidIds = new Set(kids.map((k) => k.id));
      onChange(items
        .filter((it) => it.id !== id)
        .map((it) => (
          orderById[it.id] == null
            ? it
            : {
              ...it,
              parentItemId: kidIds.has(it.id) ? (item.parentItemId || null) : it.parentItemId,
              sortOrder: orderById[it.id],
            }
        )));
      return;
    }
    const removeIds = new Set(collectDescendantIds(id, childrenMap));
    onChange(items.filter((it) => !removeIds.has(it.id)));
  };

  const commitHeading = (name) => {
    const title = (name || '').trim();
    const draft = headingDraft;
    setHeadingDraft(null);
    if (!title || !draft) return;
    const anchor = items.find((it) => it.id === draft.anchorId);
    if (!anchor) return;
    const id = uuidv4();
    if (draft.placement === 'child') {
      const kids = childrenMap[anchor.id] || [];
      const sortOrder = kids.reduce((max, kid) => Math.max(max, kid.sortOrder || 0), 0) + 1;
      onChange([
        ...items,
        {
          id,
          folderId: null,
          parentItemId: anchor.id,
          titleOverride: title,
          sortOrder,
          included: true,
          includeTestComparison: false,
        },
      ]);
      setExpanded((prev) => (prev.includes(anchor.id) ? prev : [...prev, anchor.id]));
      return;
    }
    if (draft.placement === 'parent') {
      onChange([
        ...items.map((it) => (
          it.id === anchor.id
            ? { ...it, parentItemId: id, sortOrder: 1 }
            : it
        )),
        {
          id,
          folderId: null,
          parentItemId: anchor.parentItemId || null,
          titleOverride: title,
          sortOrder: anchor.sortOrder ?? 1,
          included: true,
          includeTestComparison: false,
        },
      ]);
      return;
    }
    const parentKey = anchor.parentItemId || '';
    const siblings = childrenMap[parentKey] || [];
    const idx = siblings.findIndex((s) => s.id === anchor.id);
    const heading = {
      id,
      folderId: null,
      parentItemId: anchor.parentItemId || null,
      titleOverride: title,
      sortOrder: 0,
      included: true,
      includeTestComparison: false,
    };
    const next = [
      ...siblings.slice(0, idx + 1),
      heading,
      ...siblings.slice(idx + 1),
    ];
    const orderById = {};
    next.forEach((s, i) => { orderById[s.id] = i + 1; });
    onChange([
      ...items.map((it) => (
        orderById[it.id] == null ? it : { ...it, sortOrder: orderById[it.id] }
      )),
      { ...heading, sortOrder: orderById[id] },
    ]);
  };

  const handleDrop = (sourceId, targetId, position) => {
    if (!sourceId || !targetId) return;
    onChange(applyDrop(items, sourceId, targetId, position));
    if (position === 'inside') {
      setExpanded((prev) => (prev.includes(targetId) ? prev : [...prev, targetId]));
    }
    endDragVisual();
  };

  const moveSibling = (itemId, direction) => {
    const item = items.find((it) => it.id === itemId);
    if (!item) return;
    const siblings = childrenMap[item.parentItemId || ''] || [];
    const idx = siblings.findIndex((s) => s.id === itemId);
    const neighbor = siblings[idx + (direction === 'up' ? -1 : 1)];
    if (!neighbor) return;
    onChange(applyDrop(items, itemId, neighbor.id, direction === 'up' ? 'before' : 'after'));
  };

  const exitMoveMode = () => {
    setMoveMode(false);
    setMoveSourceId(null);
    setMoveTarget(null);
  };

  const pickMoveRow = (item, anchorEl) => {
    if (!moveSourceId) {
      setMoveSourceId(item.id);
      return;
    }
    if (moveSourceId === item.id) {
      setMoveSourceId(null);
      return;
    }
    setMoveTarget({ item, anchorEl });
  };

  const canDropOnTarget = (() => {
    if (!moveSourceId || !moveTarget?.item) return false;
    if (moveSourceId === moveTarget.item.id) return false;
    const descendants = new Set(collectDescendantIds(moveSourceId, childrenMap));
    return !descendants.has(moveTarget.item.id);
  })();

  const commitMove = (position) => {
    if (!moveSourceId || !moveTarget?.item || !canDropOnTarget) return;
    onChange(applyDrop(items, moveSourceId, moveTarget.item.id, position));
    if (position === 'inside') {
      const targetId = moveTarget.item.id;
      setExpanded((prev) => (prev.includes(targetId) ? prev : [...prev, targetId]));
    }
    setMoveTarget(null);
    setMoveSourceId(null);
  };

  const muiTheme = useTheme();
  const compact = useMediaQuery(muiTheme.breakpoints.down('md'));

  const beginRename = (it, defer = false) => {
    if (!it) return;
    if (defer) {
      pendingRenameRef.current = it;
      return;
    }
    pendingRenameRef.current = null;
    setInlineRenameId(it.id);
    setSelectedId(it.id);
  };

  const commitInlineRename = (name) => {
    const it = items.find((item) => item.id === inlineRenameId);
    setInlineRenameId(null);
    if (!it) return;
    const next = (name || '').trim();
    if (isHeadingItem(it)) {
      if (next) onUpdate(it.id, { titleOverride: next });
    } else {
      onUpdate(it.id, { titleOverride: next || null });
    }
  };

  const indentSelected = () => {
    const item = items.find((it) => it.id === selectedId);
    if (!item) return;
    const siblings = childrenMap[item.parentItemId || ''] || [];
    const idx = siblings.findIndex((s) => s.id === item.id);
    const previous = siblings[idx - 1];
    if (!previous) return;
    onChange(applyDrop(items, item.id, previous.id, 'inside'));
    setExpanded((prev) => (prev.includes(previous.id) ? prev : [...prev, previous.id]));
  };

  const outdentSelected = () => {
    const item = items.find((it) => it.id === selectedId);
    if (!item?.parentItemId) return;
    onChange(applyDrop(items, item.id, item.parentItemId, 'after'));
  };

  const rowProps = {
    onUpdate,
    onToggleIncluded,
    onToggleComparison,
    childrenMap,
    items,
    onContextMenu: (e, it, fromTouch = false) => {
      if (fromTouch) {
        ignoreMenuClose.current = true;
        setTimeout(() => { ignoreMenuClose.current = false; }, 500);
      }
      setMenu({
        mouseX: e.clientX,
        mouseY: e.clientY,
        item: it,
      });
    },
    dragIdRef,
    onDragStart: () => {
      if (listRef.current) listRef.current.style.paddingBottom = '28px';
    },
    onDragEnd: endDragVisual,
    onDragOverRow: markDrop,
    onDropOnRow: handleDrop,
    selectedId,
    inlineRenameId,
    onSelect: (id) => {
      setSelectedId(id);
      listRef.current?.focus({ preventScroll: true });
    },
    onBeginRename: beginRename,
    onCommitRename: commitInlineRename,
    onCancelRename: () => setInlineRenameId(null),
    onPreview,
    previewLoadingId,
    compact,
    onMoveSibling: compact && !moveMode ? moveSibling : null,
    moveMode: compact && moveMode,
    moveSourceId,
    onPickRow: compact && moveMode ? pickMoveRow : null,
  };

  if (!items.length) {
    return (
      <Box
        sx={{
          height: 200,
          overflow: 'hidden',
          ...(bare
            ? { p: 0.5 }
            : { border: 1, borderColor: 'divider', borderRadius: 1, p: 0.5 }),
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
          {sendMode
            ? '발송할 목차 항목이 없습니다.'
            : '서류를 선택한 뒤 「추가」하세요.'}
        </Typography>
      </Box>
    );
  }

  const moveControls = compact ? (
    <>
      {moveMode && (
        <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
          {moveSourceId ? '놓을 항목을 누르세요.' : '옮길 항목을 누르세요.'}
        </Typography>
      )}
      <Button
        size="small"
        variant={moveMode ? 'contained' : 'outlined'}
        onClick={() => (moveMode ? exitMoveMode() : setMoveMode(true))}
        sx={{
          flexShrink: 0,
          minHeight: 28,
          py: 0.25,
          px: 1,
          fontSize: '0.8125rem',
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
        }}
      >
        {moveMode ? '이동 끝내기' : '목차 이동'}
      </Button>
    </>
  ) : null;

  return (
    <Box>
      {moveControls && moveSlotEl && createPortal(moveControls, moveSlotEl)}
      {moveControls && !moveSlotEl && !preferHeaderMove && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75, px: 0.5, pb: 0.5 }}>
          {moveControls}
        </Box>
      )}
      <Box
        ref={listRef}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key !== 'Tab' || !selectedId) return;
          if (e.target.closest('input, textarea')) return;
          e.preventDefault();
          if (e.shiftKey) outdentSelected();
          else indentSelected();
        }}
        onDragOver={(e) => {
          if (e.target !== e.currentTarget) return;
          if (!dragIdRef.current) return;
          e.preventDefault();
          clearDropMark();
        }}
        onDrop={(e) => {
          if (e.target !== e.currentTarget) return;
          const id = e.dataTransfer.getData('text/plain') || dragIdRef.current;
          const last = roots[roots.length - 1];
          if (!id || !last) return;
          e.preventDefault();
          handleDrop(id, last.id, 'after');
        }}
        sx={{
          minHeight: 200,
          p: 0.5,
          outline: 'none',
          ...(bare
            ? {}
            : { border: 1, borderColor: 'divider', borderRadius: 1 }),
        }}
      >
        <TocBranch
          nodes={roots}
          childrenMap={childrenMap}
          expandedSet={expandedSet}
          onToggleExpand={(id) => {
            setExpanded((prev) => (
              prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
            ));
          }}
          draft={headingDraft}
          onCommitDraft={commitHeading}
          onCancelDraft={() => setHeadingDraft(null)}
          {...rowProps}
        />
      </Box>

      <Menu
        open={Boolean(menu)}
        onClose={() => {
          if (ignoreMenuClose.current) return;
          setMenu(null);
        }}
        anchorReference="anchorPosition"
        anchorPosition={menu ? { top: menu.mouseY, left: menu.mouseX } : undefined}
        TransitionProps={{
          onExited: () => {
            const pending = pendingDraftRef.current;
            const rename = pendingRenameRef.current;
            pendingDraftRef.current = null;
            pendingRenameRef.current = null;
            if (pending) setHeadingDraft(pending);
            if (rename) {
              setInlineRenameId(rename.id);
              setSelectedId(rename.id);
            }
          },
        }}
      >
        {!sendMode && (
          <>
            <MenuItem
              onClick={() => {
                const id = menu?.item?.id;
                if (id) pendingDraftRef.current = { anchorId: id, placement: 'parent' };
                setMenu(null);
              }}
            >
              상위 제목 추가
            </MenuItem>
            <MenuItem
              onClick={() => {
                const id = menu?.item?.id;
                if (!id) return;
                pendingDraftRef.current = { anchorId: id, placement: 'child' };
                setExpanded((prev) => (prev.includes(id) ? prev : [...prev, id]));
                setMenu(null);
              }}
            >
              하위에 제목 추가
            </MenuItem>
            <MenuItem
              onClick={() => {
                const id = menu?.item?.id;
                if (id) pendingDraftRef.current = { anchorId: id, placement: 'after' };
                setMenu(null);
              }}
            >
              아래에 제목 추가
            </MenuItem>
            <Divider />
          </>
        )}
        <MenuItem
          onClick={() => {
            const it = menu?.item;
            setMenu(null);
            beginRename(it, true);
          }}
        >
          이름 변경
        </MenuItem>
        {!sendMode && (
          <>
            <Divider />
            <MenuItem
              sx={{ color: 'error.main' }}
              onClick={() => {
                const id = menu?.item?.id;
                setMenu(null);
                if (id) onRemove(id);
              }}
            >
              제거
            </MenuItem>
          </>
        )}
      </Menu>

      <Menu
        anchorEl={moveTarget?.anchorEl}
        open={Boolean(moveTarget)}
        onClose={() => setMoveTarget(null)}
      >
        {canDropOnTarget ? (
          [
            <MenuItem key="before" onClick={() => commitMove('before')}>위</MenuItem>,
            <MenuItem key="inside" onClick={() => commitMove('inside')}>안으로</MenuItem>,
            <MenuItem key="after" onClick={() => commitMove('after')}>아래</MenuItem>,
          ]
        ) : (
          <MenuItem disabled>하위 항목으로는 옮길 수 없습니다.</MenuItem>
        )}
      </Menu>

    </Box>
  );
};

export default SubMatTemplateTocTree;
